import React from 'react';
import { X, UserCircle2, FileText, CheckCircle2, XCircle } from 'lucide-react';

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: '2-digit',
  });
};

const formatHeaderDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
};

const STATUS_STYLES = {
  Pending: 'bg-yellow-100 text-yellow-800',
  Approved: 'bg-green-100 text-green-800',
  Rejected: 'bg-red-100 text-red-800',
};

const LeaveRequestDetailModal = ({ request, onClose, onApprove, onReject }) => {
  const isPending = ['Pending', 'Submitted', 'Waiting'].includes(request.status);
  // Opened from a calendar day cell: we only have that single day's entry, not
  // the full multi-day request (leaveLines, leaveTypeCode, requestedBy, etc.
  // don't exist for it) — render a focused single-day summary instead of the
  // multi-line Leave Details table, which would otherwise always render empty.
  const isSingleDay = !request.leaveLines;
  const totalAmount = isSingleDay
    ? (request.unit === 'Hours' ? `${request.days} hrs` : `${request.days} day${request.days === 1 ? '' : 's'}`)
    : (request.unit === 'Hours' ? `${request.totalHours} hrs` : `${request.days} days`);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Leave request detail for ${request.employeeName}`}
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <UserCircle2 className="w-6 h-6 text-indigo-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm sm:text-base font-semibold text-gray-900 leading-snug">
                {request.employeeName} &ndash; {request.leaveTypeCode || request.leaveTypeName}
                <span className="font-normal text-gray-500">
                  {' '}
                  {isSingleDay
                    ? `(${formatHeaderDate(request.startDate)})`
                    : `(${formatHeaderDate(request.startDate)} to ${formatHeaderDate(request.endDate)})`}
                </span>
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {isSingleDay ? 'Single-Day Detail' : 'Detailed View'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100 flex-shrink-0 ml-2"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 p-6 space-y-6">

          {/* Action + Status row */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-gray-50 rounded-lg border border-gray-100">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Status:</span>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[request.status] ?? 'bg-gray-100 text-gray-800'}`}>
                {request.status === 'Pending' ? 'Pending Approval' : request.status}
              </span>
            </div>
            {isPending && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Action:</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { onApprove(request.id); onClose(); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => { onReject(request.id); onClose(); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                  >
                    <XCircle className="w-4 h-4" />
                    Reject
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Summary section */}
          <div>
            <div className={`grid grid-cols-2 ${isSingleDay ? 'md:grid-cols-3' : 'md:grid-cols-4'} gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden`}>
              {[
                { label: isSingleDay ? 'Leave Date' : 'Requested Date', value: formatDate(isSingleDay ? request.startDate : request.requestedDate) },
                { label: 'Employee', value: request.employeeName },
                { label: 'Total Amount', value: totalAmount },
                ...(isSingleDay ? [] : [{ label: 'Requested By', value: request.requestedBy }]),
              ].map(({ label, value }) => (
                <div key={label} className="bg-white px-4 py-3">
                  <p className="text-xs font-semibold text-gray-700 mb-1">{label}</p>
                  <p className="text-sm text-gray-900">{value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Leave Details section */}
          <div>
            <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              Leave Details
            </h4>

            {isSingleDay ? (
              // Single day clicked on the calendar: show that one entry's
              // details directly rather than a multi-row table designed for
              // a full multi-day request (whose per-line data isn't available here).
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                {[
                  { label: 'Leave Type', value: request.leaveTypeName },
                  { label: 'Date', value: formatDate(request.startDate) },
                  { label: 'Unit', value: request.unit || 'Days' },
                  { label: 'Amount', value: request.days },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-white px-4 py-3">
                    <p className="text-xs font-semibold text-gray-700 mb-1">{label}</p>
                    <p className="text-sm text-gray-900">{value}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="grid grid-cols-5 bg-gray-50 border-b border-gray-200">
                  {['Leave Type', 'Leave Description', 'Unit', 'Date', 'Amount'].map((col) => (
                    <div key={col} className="px-3 py-2 text-xs font-semibold text-gray-700 border-r last:border-r-0 border-gray-200">
                      {col}
                    </div>
                  ))}
                </div>
                {request.leaveLines.length > 0 ? (
                  request.leaveLines.map((line, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-5 border-b last:border-b-0 border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <div className="px-3 py-2.5 text-sm text-gray-800 border-r border-gray-100">{request.leaveTypeCode}</div>
                      <div className="px-3 py-2.5 text-sm text-gray-800 border-r border-gray-100">{request.leaveTypeDescription}</div>
                      <div className="px-3 py-2.5 text-sm text-gray-800 border-r border-gray-100">{request.unit}</div>
                      <div className="px-3 py-2.5 text-sm text-gray-800 border-r border-gray-100">{formatDate(line.date)}</div>
                      <div className="px-3 py-2.5 text-sm text-gray-800">{line.amount}</div>
                    </div>
                  ))
                ) : (
                  <div className="px-3 py-6 text-center text-sm text-gray-400">No leave lines available.</div>
                )}
              </div>
            )}
          </div>

          {/* Reason */}
          {request.reason && (
            <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
              <p className="text-xs font-semibold text-indigo-700 mb-1 uppercase tracking-wide">Reason</p>
              <p className="text-sm text-gray-800">{request.reason}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 flex-shrink-0 bg-gray-50 rounded-b-xl">
          <p className="text-xs text-gray-400">Leave request data is sourced from LeaveRequestsV2 / EssLeaveRequestHeaders.</p>
        </div>
      </div>
    </div>
  );
};

export default LeaveRequestDetailModal;
